import { friendlyError } from "@/lib/friendly-errors";
import { PracticeSafetyNote } from "@/components/safety/SafetyNotices";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { ArrowLeft, Check, Waves, Sparkles, Heart, Clock } from "lucide-react";
import { type Practice } from "@/lib/somatic-practices";
import { useMembership } from "@/hooks/useMembership";
import { MemberUpgradeCard } from "@/components/MemberUpgradeCard";
import { FavoriteButton } from "@/components/FavoriteButton";
import { StepCarousel } from "@/components/somatic/StepCarousel";
import { useToolkitPractices } from "@/hooks/useToolkitPractices";
import { VISUAL_STEPS } from "@/lib/somatic-visual-steps";

export const Route = createFileRoute("/somatic_/$practice")({
  loader: async ({ params }): Promise<{ practice: Practice | null }> => {
    const { getPractice } = await import("@/lib/somatic-practices");
    if (!/^[a-z0-9-]{1,80}$/.test(params.practice)) throw notFound();
    // Unknown slugs may be practices added in Admin → Toolkit; resolved client-side.
    return { practice: getPractice(params.practice) ?? null };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Practice not found — Recovery Trauma™" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const p = loaderData.practice ?? { title: "Somatic Exercise", blurb: "A self-guided somatic exercise from Recovery Trauma™." };
    const url = `https://recoverytrauma.com/somatic/${params.practice}`;
    return {
      meta: [
        { title: `${p.title} — Somatic Exercises — Recovery Trauma™` },
        { name: "description", content: p.blurb },
        { property: "og:title", content: `${p.title} — Recovery Trauma™` },
        { property: "og:description", content: p.blurb },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: withMemberGate(PracticePage, "Somatic Practice"),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-display text-2xl text-foreground">Practice not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This somatic practice doesn’t exist.
      </p>
      <Link
        to="/somatic"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-semibold text-foreground shadow-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Somatic Exercises
      </Link>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-display text-xl text-foreground">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {friendlyError(error, "This practice couldn't load just now. Please try again.")}
      </p>
      <button
        type="button"
        onClick={reset}
        className="mt-4 rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-semibold text-foreground"
      >
        Try again
      </button>
    </div>
  ),
});

function PracticePage() {
  const { practice } = Route.useLoaderData();
  const { practice: slug } = Route.useParams();
  const { data: dbItems, isLoading } = useToolkitPractices();
  if (practice) return <PracticeView practice={practice} />;
  const found = dbItems?.find((p) => p.id === slug && p.kind === "somatic");
  if (found) return <PracticeView practice={found} />;
  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-display text-2xl text-foreground">{isLoading ? "Loading…" : "Practice not found"}</h1>
      {!isLoading && (
        <Link to="/somatic" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-foreground underline">
          <ArrowLeft className="h-4 w-4" /> Back to Somatic Exercises
        </Link>
      )}
    </div>
  );
}

function PracticeView({ practice }: { practice: Practice }) {
  const { isMember } = useMembership();
  const locked = practice.tier === "member" && !isMember;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-10">
      {/* Back link */}
      <Link
        to="/somatic"
        className="mb-5 inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All Somatic Exercises
      </Link>

      {/* Header */}
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">
            <Waves className="h-3.5 w-3.5" /> Somatic Exercise
          </div>
          <h1 className="font-display text-2xl leading-tight tracking-tight text-foreground sm:text-3xl md:text-4xl">
            <span aria-hidden className="me-2">{practice.emoji}</span>
            {practice.title}
          </h1>
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Clock className="h-3 w-3" /> {practice.duration} · Self-guided
          </p>
        </div>
        <FavoriteButton
          storageKey="rt:fav-somatic"
          id={practice.id}
          label={practice.title}
          sub={practice.duration}
          to={`/somatic/${practice.id}`}
          size="md"
        />
      </header>

      {locked ? (
        <MemberUpgradeCard
          title="This somatic practice is a Member resource"
          body="Continue your wellbeing journey with full Member access to all body-based practices."
        />
      ) : (
        <>
          {/* Step-by-step carousel, or single illustration for one-position practices */}
          {VISUAL_STEPS[practice.id] ? (
            <StepCarousel slides={VISUAL_STEPS[practice.id]} title={practice.title} />
          ) : practice.image ? (
            <div className="mb-6 overflow-hidden rounded-3xl bg-[oklch(0.96_0.03_88)] shadow-sm">
              <img
                src={practice.image}
                alt={practice.title}
                width={1024}
                height={1024}
                loading="eager"
                className="mx-auto h-72 w-full object-contain sm:h-96"
              />
            </div>
          ) : null}

          {/* Introduction */}
          <section className="mb-8 space-y-3 rounded-2xl border border-border/50 bg-card px-5 py-5">
            <p className="text-[15px] leading-relaxed text-foreground/90">
              {practice.blurb}
            </p>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              {practice.what}
            </p>
          </section>

          {/* Why it can help */}
          {practice.why && <section className="mb-8">
            <h2 className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              <Heart className="h-3 w-3" /> Why it can help
            </h2>
            <p className="text-[14px] leading-relaxed text-foreground/85">
              {practice.why}
            </p>
          </section>}

          <PracticeSafetyNote kind="somatic" className="mb-6" />

          {/* Steps — one long scroll, no wizard */}
          <section className="mb-8">
            <h2 className="mb-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              <Sparkles className="h-3 w-3" /> The practice
            </h2>
            <ol className="space-y-4">
              {practice.steps.map((step: string, i: number) => (
                <li
                  key={i}
                  className="overflow-hidden rounded-2xl border border-border/50 bg-card"
                >
                  <div className="flex items-start gap-4 px-5 py-5">
                    <span
                      aria-hidden
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--gradient-gold)] font-display text-base font-semibold text-foreground shadow-sm"
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1 pt-1.5">
                      <p className="text-[15px] leading-relaxed text-foreground/90">
                        {step}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Tips */}
          {practice.tips && practice.tips.length > 0 && (
            <section className="mb-8 rounded-2xl border border-lavender/30 bg-lavender/10 px-5 py-5">
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                Helpful tips
              </h2>
              <ul className="space-y-1.5 text-[13px] leading-relaxed text-foreground/85">
                {practice.tips.map((t: string, i: number) => (
                  <li key={i} className="flex gap-2">
                    <span aria-hidden className="text-[var(--gold-deep)]">·</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* When to use */}
          {practice.whenToUse && (
            <section className="mb-10">
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                When to use it
              </h2>
              <p className="text-[14px] leading-relaxed text-foreground/85">
                {practice.whenToUse}
              </p>
            </section>
          )}

          {/* Finish */}
          <div className="mb-6 flex flex-col items-center gap-3">
            <Link
              to="/somatic"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:opacity-90"
            >
              <Check className="h-4 w-4" /> Finish Exercise
            </Link>
            <p className="text-center text-[11px] text-muted-foreground">
              Take a slow breath. Choice is always yours.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
